'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export interface BookingFlowCompanionDraft {
  dateOfBirth: string;
  firstName: string;
  id?: string | null;
  lastName: string;
}

export interface BookingFlowTravelersStepData {
  [key: string]: any;
  companionsToCreateByPaxHash?: Record<string, BookingFlowCompanionDraft>;
}

export interface BookingFlowSteps {
  payment: any;
  stay: any;
  summary: any;
  travelers: BookingFlowTravelersStepData;
  trip: any;
}

export interface ModuleStatus {
  id: string;
  isComplete: boolean;
  isRequired: boolean;
}

export type ModuleStatusMap = Record<string, ModuleStatus>;

export interface BookingFlowContextState {
  activeAnchor: string | null;
  activeBookingStep: number | null;
  anchorSignal: number;
  bookingFlowSteps: Content.Components['BookingFlowModuleDto']['steps'] | null;
  clearCompanionsToCreate: () => void;
  isNavigationBlocked: boolean;
  moduleStatusMap: ModuleStatusMap;
  registerNavigationBlocker: (id: string, blocked: boolean) => void;
  setActiveAnchor: (anchor: string | null) => void;
  setActiveBookingStep: (step: number | null) => void;
  setBookingFlowSteps: (
    steps: Content.Components['BookingFlowModuleDto']['steps'] | null,
  ) => void;
  setCompanionToCreate: (
    uniquePaxHash: string,
    value: BookingFlowCompanionDraft | null,
  ) => void;
  stepsData: BookingFlowSteps;
  updateModuleStatus: (
    id: string,
    status: Partial<Pick<ModuleStatus, 'isComplete' | 'isRequired'>>,
  ) => void;
  updateStepData: (
    stepKey: keyof BookingFlowSteps,
    field: string,
    value: any,
  ) => void;
}

interface BookingFlowContextProviderProps {
  children: ReactNode;
  initialData?: {
    activeAnchor: string | null;
    activeBookingStep: number | null;
    bookingFlowSteps:
      | Content.Components['BookingFlowModuleDto']['steps']
      | null;
    clearCompanionsToCreate: () => void;
    moduleStatusMap: ModuleStatusMap;
    setActiveAnchor: (anchor: string | null) => void;
    setActiveBookingStep: (step: number | null) => void;
    setBookingFlowSteps: (
      steps: Content.Components['BookingFlowModuleDto']['steps'] | null,
    ) => void;
    setCompanionToCreate: (
      uniquePaxHash: string,
      value: BookingFlowCompanionDraft | null,
    ) => void;
    stepsData: BookingFlowSteps;
    updateModuleStatus: (
      id: string,
      status: Partial<Pick<ModuleStatus, 'isComplete' | 'isRequired'>>,
    ) => void;
    updateStepData: (
      stepKey: keyof BookingFlowSteps,
      field: string,
      value: any,
    ) => void;
  };
}

const BookingFlowContext = createContext<BookingFlowContextState | undefined>(
  undefined,
);

export const BookingFlowContextProvider = ({
  children,
  initialData,
}: BookingFlowContextProviderProps) => {
  const [stepsData, setStepsData] = useState<BookingFlowSteps>(
    initialData?.stepsData || {
      payment: {},
      stay: {},
      summary: {},
      travelers: {},
      trip: {},
    },
  );

  const [activeBookingStep, setActiveBookingStep] = useState<number | null>(
    initialData?.activeBookingStep ?? null,
  );
  const [moduleStatusMap, setModuleStatusMap] = useState<ModuleStatusMap>({});
  const [activeAnchor, setActiveAnchor] = useState<string | null>(null);
  const [anchorSignal, setAnchorSignal] = useState(0);
  const [bookingFlowSteps, setBookingFlowSteps] = useState<
    Content.Components['BookingFlowModuleDto']['steps'] | null
  >(initialData?.bookingFlowSteps ?? null);

  const [navigationBlockers, setNavigationBlockers] = useState<Set<string>>(
    new Set(),
  );
  const isNavigationBlocked = useMemo(
    () => navigationBlockers.size > 0,
    [navigationBlockers],
  );

  // Prevent user from proceeding to the next step while debounced updates are still pending
  const registerNavigationBlocker = useCallback(
    (id: string, blocked: boolean) => {
      setNavigationBlockers(previous => {
        const collection = new Set(previous);

        blocked ? collection.add(id) : collection.delete(id);

        return collection;
      });
    },
    [],
  );

  const updateStepData = (
    stepKey: keyof BookingFlowSteps,
    field: string,
    value: any,
  ) => {
    setStepsData(prev => ({
      ...prev,
      [stepKey]: {
        ...prev[stepKey],
        [field]: value,
      },
    }));
  };

  const updateModuleStatus = (
    id: string,
    status: Partial<Pick<ModuleStatus, 'isComplete' | 'isRequired'>>,
  ) => {
    setModuleStatusMap(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        ...status,
        id,
      },
    }));
  };

  const setCompanionToCreate = useCallback(
    (uniquePaxHash: string, value: BookingFlowCompanionDraft | null) => {
      setStepsData(previousValue => {
        const currentDrafts =
          previousValue.travelers.companionsToCreateByPaxHash ?? {};
        const nextDrafts = { ...currentDrafts };

        if (value) {
          nextDrafts[uniquePaxHash] = value;
        } else {
          delete nextDrafts[uniquePaxHash];
        }

        return {
          ...previousValue,
          travelers: {
            ...previousValue.travelers,
            companionsToCreateByPaxHash: nextDrafts,
          },
        };
      });
    },
    [],
  );

  const clearCompanionsToCreate = useCallback(() => {
    setStepsData(previousValue => ({
      ...previousValue,
      travelers: {
        ...previousValue.travelers,
        companionsToCreateByPaxHash: {},
      },
    }));
  }, []);

  // anchorSignal is relevant in order to trigger the scroll continuously even if the same anchor is set
  const triggerAnchor = (id: string | null) => {
    setActiveAnchor(id);
    setAnchorSignal(prev => prev + 1);
  };

  return (
    <BookingFlowContext.Provider
      value={{
        activeAnchor,
        activeBookingStep,
        anchorSignal,
        bookingFlowSteps,
        clearCompanionsToCreate,
        isNavigationBlocked,
        moduleStatusMap,
        registerNavigationBlocker,
        setActiveAnchor: triggerAnchor,
        setActiveBookingStep,
        setBookingFlowSteps,
        setCompanionToCreate,
        stepsData,
        updateModuleStatus,
        updateStepData,
      }}
    >
      {children}
    </BookingFlowContext.Provider>
  );
};

export const useBookingFlowContext = () => {
  const context = useContext(BookingFlowContext);

  if (!context) {
    throw new Error(
      'useBookingFlowContext must be used within a BookingFlowContextProvider',
    );
  }

  return context;
};
